import type { QueryClient } from "@tanstack/react-query";
import type {
  BusinessFeature,
  BusinessListItem,
  BusinessProfileBusiness,
  BusinessProfilesResponse,
} from "../../services/businessProfileService";

interface DataContainer {
  data?: unknown;
}

interface BusinessListContainer extends DataContainer {
  businesses?: unknown;
  results?: unknown;
}

export const businessProfilesQueryKey = ["businessProfiles"] as const;

export const EMPTY_BUSINESS_PROFILES: BusinessProfilesResponse = {
  personal: {
    type: "personal",
    displayName: "",
    avatarUrl: null,
  },
  businesses: [],
  activeBusinessId: null,
};

export const BUSINESS_CONTEXT_DEPENDENT_QUERY_PREFIXES = [
  ["acceptedCurrencies"],
  ["dposSessionQr"],
  ["dposSessionReceipt"],
  ["dposSessions"],
  ["dposPerformance"],
  ["dposCashierModeStatus"],
  ["payLinks"],
  ["businessWallet"],
  ["businessAvailableAssets"],
  ["businessAvailableBalances"],
  ["addressBook"],
  ["cryptoAddress"],
  ["businessMe"],
] as const;

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return Boolean(value) && typeof value === "object";
};

const asString = (value: unknown): string | undefined => {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
};

const normalizeString = (value: string | null | undefined): string => {
  return (value || "").trim();
};

const normalizeBusinessName = (value: string | null | undefined): string => {
  return normalizeString(value).toLowerCase();
};

const getNestedData = (value: unknown): unknown => {
  return isRecord(value) ? value.data : undefined;
};

const getLogoString = (value: unknown): string | undefined => {
  if (typeof value === "string") {
    return asString(value);
  }

  if (isRecord(value)) {
    return asString(value.url);
  }

  return undefined;
};

const getBusinessRecord = (item: BusinessListItem): Record<string, unknown> => {
  if (item.business && isRecord(item.business)) {
    return item.business;
  }

  return item as Record<string, unknown>;
};

const normalizeFeatures = (value: unknown): BusinessFeature[] => {
  return Array.isArray(value) ? (value as BusinessFeature[]) : [];
};

const normalizeRole = (
  value: unknown,
  roleKey: string,
  roleName: string
): BusinessProfileBusiness["role"] => {
  if (isRecord(value)) {
    const key = asString(value.key) || roleKey;
    const name = asString(value.name) || roleName;
    const features = normalizeFeatures(value.features);

    return key || name
      ? {
          key,
          name,
          ...(features.length > 0 ? { features } : {}),
        }
      : null;
  }

  return roleKey || roleName
    ? {
        key: roleKey,
        name: roleName,
      }
    : null;
};

const normalizeProfileBusiness = (
  value: unknown
): BusinessProfileBusiness | null => {
  if (!isRecord(value)) {
    return null;
  }

  const businessId =
    asString(value.businessId) || asString(value.id) || asString(value._id);
  const businessName = asString(value.businessName);

  if (!businessId || !businessName) {
    return null;
  }

  const roleKey = asString(value.roleKey) || "";
  const roleName = asString(value.roleName) || "";
  const roleFeatures = isRecord(value.role)
    ? normalizeFeatures(value.role.features)
    : [];
  const features = normalizeFeatures(value.features);

  return {
    type: "business",
    businessId,
    businessName,
    logoUrl: asString(value.logoUrl) || getLogoString(value.logo),
    avatarUrl: asString(value.avatarUrl),
    logo: getLogoString(value.logo),
    imageUrl: asString(value.imageUrl),
    roleKey,
    roleName,
    role: normalizeRole(value.role, roleKey, roleName),
    features: features.length > 0 ? features : roleFeatures,
    kybStatus: asString(value.kybStatus) || asString(value.status) || "pending",
  };
};

const isBusinessProfilesPayload = (
  value: unknown
): value is BusinessProfilesResponse => {
  return isRecord(value) && Array.isArray(value.businesses);
};

const normalizePersonalProfile = (
  value: unknown
): BusinessProfilesResponse["personal"] => {
  if (!isRecord(value)) {
    return EMPTY_BUSINESS_PROFILES.personal;
  }

  return {
    type: "personal",
    displayName: asString(value.displayName) || "",
    avatarUrl: asString(value.avatarUrl) || null,
  };
};

export const normalizeBusinessProfilesPayload = (
  value: unknown
): BusinessProfilesResponse => {
  if (isBusinessProfilesPayload(value)) {
    const businesses = value.businesses
      .map(normalizeProfileBusiness)
      .filter(
        (business): business is BusinessProfileBusiness => business !== null
      );

    return {
      personal: normalizePersonalProfile(value.personal),
      businesses,
      activeBusinessId: asString(value.activeBusinessId) || null,
    };
  }

  const nestedValue = getNestedData(value);

  if (nestedValue !== undefined && nestedValue !== value) {
    return normalizeBusinessProfilesPayload(nestedValue);
  }

  return EMPTY_BUSINESS_PROFILES;
};

export const normalizeBusinessList = (value: unknown): BusinessListItem[] => {
  if (Array.isArray(value)) {
    return value as BusinessListItem[];
  }

  if (!isRecord(value)) {
    return [];
  }

  const nestedValue = (value as BusinessListContainer).data;

  if (Array.isArray(nestedValue)) {
    return nestedValue as BusinessListItem[];
  }

  if (nestedValue !== undefined && nestedValue !== value) {
    return normalizeBusinessList(nestedValue);
  }

  const container = value as BusinessListContainer;

  if (Array.isArray(container.businesses)) {
    return container.businesses as BusinessListItem[];
  }

  if (Array.isArray(container.results)) {
    return container.results as BusinessListItem[];
  }

  return [];
};

export const resolveBusinessLogoUrl = (
  business: BusinessProfileBusiness | BusinessListItem | null | undefined
): string | undefined => {
  if (!business) {
    return undefined;
  }

  const record =
    "business" in business && business.business
      ? getBusinessRecord(business)
      : (business as Record<string, unknown>);

  return (
    asString(record.logoUrl) ||
    asString(record.avatarUrl) ||
    getLogoString(record.logo) ||
    asString(record.imageUrl)
  );
};

const createBusinessIndex = (businessList: BusinessListItem[]) => {
  const byId = new Map<string, BusinessListItem>();
  const byName = new Map<string, BusinessListItem>();

  businessList.forEach((item) => {
    const businessRecord = getBusinessRecord(item);
    const businessId = normalizeString(
      asString(businessRecord._id) ||
        asString(businessRecord.id) ||
        asString(businessRecord.businessId)
    );
    const businessName = normalizeBusinessName(
      asString(businessRecord.businessName)
    );

    if (businessId && !byId.has(businessId)) {
      byId.set(businessId, item);
    }

    if (businessName && !byName.has(businessName)) {
      byName.set(businessName, item);
    }
  });

  return { byId, byName };
};

export const normalizeBusinessListItem = (
  item: BusinessListItem
): BusinessProfileBusiness | null => {
  const businessRecord = getBusinessRecord(item);
  const businessId =
    asString(businessRecord._id) ||
    asString(businessRecord.id) ||
    asString(businessRecord.businessId);
  const businessName = asString(businessRecord.businessName);

  if (!businessId || !businessName) {
    return null;
  }

  const roleValue = item.role || businessRecord.role;
  const roleRecord = isRecord(roleValue) ? roleValue : null;
  const roleKey =
    item.roleKey ||
    item.role?.key ||
    asString(businessRecord.roleKey) ||
    asString(roleRecord?.key) ||
    "";
  const roleName =
    item.roleName ||
    item.role?.name ||
    asString(businessRecord.roleName) ||
    asString(roleRecord?.name) ||
    "";
  const itemFeatures = normalizeFeatures(item.features);
  const businessFeatures = normalizeFeatures(businessRecord.features);
  const roleFeatures = roleRecord ? normalizeFeatures(roleRecord.features) : [];

  return {
    type: "business",
    businessId,
    businessName,
    logoUrl: resolveBusinessLogoUrl(item),
    avatarUrl: asString(businessRecord.avatarUrl),
    logo: getLogoString(businessRecord.logo),
    imageUrl: asString(businessRecord.imageUrl),
    roleKey,
    roleName,
    role: normalizeRole(roleValue, roleKey, roleName),
    features:
      itemFeatures.length > 0
        ? itemFeatures
        : businessFeatures.length > 0
          ? businessFeatures
          : roleFeatures,
    kybStatus:
      asString(businessRecord.kybStatus) ||
      asString(businessRecord.status) ||
      "pending",
  };
};

export const enrichBusinessProfiles = (
  profiles: BusinessProfilesResponse,
  businessList: BusinessListItem[]
): BusinessProfilesResponse => {
  const index = createBusinessIndex(businessList);

  const businesses =
    profiles.businesses.length > 0
      ? profiles.businesses
      : businessList
          .map(normalizeBusinessListItem)
          .filter(
            (business): business is BusinessProfileBusiness => business !== null
          );

  const enrichedBusinesses = businesses.map((business) => {
    const matchedBusiness =
      index.byId.get(normalizeString(business.businessId)) ||
      index.byName.get(normalizeBusinessName(business.businessName));

    if (!matchedBusiness) {
      return business;
    }

    const matchedProfile = normalizeBusinessListItem(matchedBusiness);
    const matchedBusinessRecord = getBusinessRecord(matchedBusiness);
    const matchedRoleKey = matchedProfile?.roleKey || "";
    const matchedRoleName = matchedProfile?.roleName || "";
    const matchedFeatures = matchedProfile?.features || [];

    return {
      ...business,
      logoUrl:
        resolveBusinessLogoUrl(business) ||
        resolveBusinessLogoUrl(matchedBusiness),
      roleKey: business.roleKey || matchedRoleKey || "",
      roleName: business.roleName || matchedRoleName || "",
      role: business.role || matchedProfile?.role || null,
      features:
        business.features.length > 0 ? business.features : matchedFeatures,
      kybStatus:
        business.kybStatus ||
        asString(matchedBusinessRecord.kybStatus) ||
        asString(matchedBusinessRecord.status) ||
        "pending",
    };
  });

  const hasActiveBusiness = enrichedBusinesses.some(
    (business) => business.businessId === profiles.activeBusinessId
  );
  const fallbackActiveBusinessId = profiles.activeBusinessId
    ? (enrichedBusinesses.find(
        (business) =>
          business.kybStatus === "approved" || business.kybStatus === "active"
      )?.businessId ??
      enrichedBusinesses[0]?.businessId ??
      null)
    : null;

  return {
    ...profiles,
    businesses: enrichedBusinesses,
    activeBusinessId: hasActiveBusiness
      ? profiles.activeBusinessId
      : fallbackActiveBusinessId,
  };
};

export const updateActiveBusinessIdInCache = (
  queryClient: QueryClient,
  activeBusinessId: string | null
): void => {
  queryClient.setQueryData<BusinessProfilesResponse | undefined>(
    businessProfilesQueryKey,
    (previous) =>
      previous
        ? {
            ...previous,
            activeBusinessId,
          }
        : previous
  );
};

export const syncActiveBusinessContext = async (
  queryClient: QueryClient,
  activeBusinessId: string | null
): Promise<void> => {
  updateActiveBusinessIdInCache(queryClient, activeBusinessId);
  await invalidateBusinessSuiteQueries(queryClient);
  await queryClient.invalidateQueries({
    queryKey: ["portfolioEstimateAssets"],
  });
  await queryClient.invalidateQueries({
    queryKey: businessProfilesQueryKey,
  });
};

export const invalidateBusinessSuiteQueries = async (
  queryClient: QueryClient
): Promise<void> => {
  await Promise.all(
    BUSINESS_CONTEXT_DEPENDENT_QUERY_PREFIXES.map((queryKey) =>
      queryClient.invalidateQueries({ queryKey, exact: false })
    )
  );
};
